import axios from 'axios';
import firebaseService from './firebaseService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      // Wait for auth to be ready
      const user = await firebaseService.waitForAuth();

      if (user) {
        const token = await firebaseService.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✓ Auth token added to request');
      } else {
        console.warn('⚠ No authenticated user - request will be sent without token');
      }
    } catch (error) {
      console.error('✗ Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const responseData = error.response.data;
      const message = responseData?.message || 'An error occurred';
      
      // Log specific error types for debugging
      if (status === 400) {
        console.error('✗ Bad Request (400):', responseData);
        // If there are validation details, include them
        if (responseData?.details) {
          console.error('Validation errors:', responseData.details);
          return Promise.reject({ 
            message, 
            status,
            details: responseData.details,
            data: responseData
          });
        }
      } else if (status === 401) {
        console.error('✗ Unauthorized request - token may be invalid or expired');
      } else if (status === 403) {
        console.error('✗ Forbidden - insufficient permissions');
      } else if (status === 404) {
        console.error('✗ Resource not found');
      }
      
      return Promise.reject({ message, status, data: responseData });
    } else if (error.request) {
      // Request made but no response
      console.error('✗ Network error - no response from server');
      return Promise.reject({ message: 'Network error. Please try again.', status: 0 });
    } else {
      // Something else happened
      console.error('✗ Request error:', error.message);
      return Promise.reject({ message: error.message, status: 0 });
    }
  }
);

// Producer API
export const producerAPI = {
  register: (data) => api.post('/producers/register', data),
  getProfile: () => api.get('/producers/profile'),
  updateProfile: (data) => api.put('/producers/profile', data),
  deleteAccount: () => api.delete('/producers/profile'),
  getByUrlFriendlyName: (urlFriendlyName) => api.get(`/producers/by-name/${urlFriendlyName}`),
  checkUrlFriendlyName: (urlFriendlyName) => api.get(`/producers/check/${urlFriendlyName}`),
  getAllUrlFriendlyNames: () => api.get('/producers/url-names'),
};

// Geocoding API
export const geocodingAPI = {
  searchAddresses: (query, country) => {
    const params = { query };
    if (country) {
      params.country = country;
    }
    return api.get('/geocoding/search', { params });
  },
};

// Products API
export const productsAPI = {
  list: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Size Types API
export const sizeTypesAPI = {
  list: () => api.get('/size-types'),
  getById: (id) => api.get(`/size-types/${id}`),
  create: (data) => api.post('/size-types', data),
  update: (id, data) => api.put(`/size-types/${id}`, data),
  delete: (id) => api.delete(`/size-types/${id}`),
};

// Size Options API
export const sizeOptionsAPI = {
  list: () => api.get('/size-options'),
  getById: (id) => api.get(`/size-options/${id}`),
  create: (data) => api.post('/size-options', data),
  update: (id, data) => api.put(`/size-options/${id}`, data),
  delete: (id) => api.delete(`/size-options/${id}`),
};

// Prices API
export const pricesAPI = {
  list: (productId) => {
    const params = productId ? { productId } : {};
    return api.get('/prices', { params });
  },
  getById: (id) => api.get(`/prices/${id}`),
  create: (data) => api.post('/prices', data),
  update: (id, data) => api.put(`/prices/${id}`, data),
  updateStock: (id, stockQuantity) => api.patch(`/prices/${id}/stock`, { stockQuantity }),
  delete: (id) => api.delete(`/prices/${id}`),
};

// Categories API
export const categoriesAPI = {
  list: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Orders API
export const ordersAPI = {
  list: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  fulfill: (id) => api.post(`/orders/${id}/fulfill`),
};

// Farm Images API
export const farmImagesAPI = {
  list: () => api.get('/farm-images'),
  getByType: (imageType) => api.get(`/farm-images/${imageType}`),
  upload: (imageType, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/farm-images/${imageType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (imageType) => api.delete(`/farm-images/${imageType}`),
};

// Product Images API
export const productImagesAPI = {
  listByProduct: (productId) => api.get(`/product-images/product/${productId}`),
  getBySizePrice: (sizePriceId) => api.get(`/product-images/size-price/${sizePriceId}`),
  upload: (sizePriceId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/product-images/size-price/${sizePriceId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (sizePriceId) => api.delete(`/product-images/size-price/${sizePriceId}`),
};

// Websites API
export const websitesAPI = {
  list: () => api.get('/websites'),
  getById: (id) => api.get(`/websites/${id}`),
  create: (data) => api.post('/websites', data),
  update: (id, data) => api.put(`/websites/${id}`, data),
  delete: (id) => api.delete(`/websites/${id}`),
};

// Claim Farm API
export const claimFarmAPI = {
  getClaimData: (internalId) => api.get(`/claim-farm/${internalId}`),
  searchUnclaimed: (filters) => api.get('/claim-farm/search/unclaimed', { params: filters }),
  listUnclaimed: (limit) => api.get('/claim-farm/list/unclaimed', { params: { limit } }),
};

// Helper functions for Edit dialogs
export const getProduct = async (productId) => {
  const response = await productsAPI.getById(productId);
  return response.data.data;
};

export const updateProduct = async (productId, data) => {
  const response = await productsAPI.update(productId, data);
  return response.data.data;
};

export const getProductSizePrice = async (productSizePriceId) => {
  const response = await pricesAPI.getById(productSizePriceId);
  return response.data.data;
};

export const updateProductSizePrice = async (productSizePriceId, data) => {
  const response = await pricesAPI.update(productSizePriceId, data);
  return response.data.data;
};

export const uploadImage = async (file) => {
  // For now, this is a placeholder. In production, you'd upload to a specific size price
  // This will need to be called with the sizePriceId context
  throw new Error('uploadImage requires sizePriceId context');
};

export const deleteImage = async (imageUrl) => {
  // Extract sizePriceId from imageUrl or context if needed
  // For now, this is a placeholder
  throw new Error('deleteImage requires sizePriceId context');
};

export default api;
